import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = '';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sid}`, { withCredentials: true });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center"
      >
        {(status === 'checking' || status === 'processing') && (
          <div data-testid="payment-processing">
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-900/50 border-2 border-green-500 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold mb-2 uppercase tracking-tight">Welcome to VIP!</h1>
            <p className="text-muted-foreground mb-2">Your VIP Pre-Sale access is now active.</p>
            <p className="text-primary font-semibold mb-8">All 155 workouts are now unlocked!</p>
            <Button
              data-testid="go-to-workouts-btn"
              onClick={() => navigate('/workouts')}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider h-12"
            >
              Start Your First Workout
            </Button>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 rounded-full bg-red-900/50 border-2 border-red-500 flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-8">
              {status === 'timeout' ? 'Payment verification timed out. If you were charged, access will be granted shortly.' : 
               status === 'expired' ? 'Your payment session expired. Please try again.' : 
               'There was an issue verifying your payment.'}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-secondary text-white hover:bg-secondary/80 rounded-sm font-bold h-12"
            >
              Back to Home
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
